import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { FundAsset, Fund } from "@/types";
import { useCreateFundAsset, useUpdateFundAsset } from "@/hooks/useFundAssets";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  asset?: FundAsset | null;
  funds: Fund[];
  defaultFundId?: string;
}

type Draft = Omit<FundAsset, "id" | "created_at" | "updated_at">;

const empty = (fundId = ""): Draft => ({
  fund_id: fundId,
  asset_name: "",
  thesis: null,
  investment_date: null,
  acquisition_cost: null,
  capital_received: null,
  future_receivables: null,
  future_asset_value: null,
  multiple: null,
  irr: null,
  first_payment_date: null,
  first_payment_value: null,
  last_payment_date: null,
  last_payment_value: null,
  average_tenor: null,
  structure: null,
  upfront_amount: null,
  other_details: null,
  feeder: null,
});

export function AssetFormDialog({ open, onOpenChange, asset, funds, defaultFundId }: Props) {
  const [draft, setDraft] = useState<Draft>(empty(defaultFundId));
  const create = useCreateFundAsset();
  const update = useUpdateFundAsset();

  useEffect(() => {
    if (asset) {
      const { id: _id, created_at: _ca, updated_at: _ua, ...rest } = asset;
      const n = (v: unknown) => (v != null && v !== "" ? Number(v) : null);
      setDraft({
        ...rest,
        acquisition_cost:    n(rest.acquisition_cost),
        capital_received:    n(rest.capital_received),
        future_receivables:  n(rest.future_receivables),
        future_asset_value:  n(rest.future_asset_value),
        multiple:            n(rest.multiple),
        irr:                 n(rest.irr),
        first_payment_value: n(rest.first_payment_value),
        last_payment_value:  n(rest.last_payment_value),
        average_tenor:       n(rest.average_tenor),
      });
    } else {
      setDraft(empty(defaultFundId));
    }
  }, [asset, open, defaultFundId]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const numField = (k: keyof Draft, label: string) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        step="any"
        value={draft[k] ?? ""}
        onChange={(e) =>
          set(k, (e.target.value === "" ? null : Number(e.target.value)) as Draft[typeof k])
        }
        className="h-8"
      />
    </div>
  );

  const dateField = (k: keyof Draft, label: string) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type="date"
        value={draft[k] ? String(draft[k]).slice(0, 10) : ""}
        onChange={(e) => set(k, (e.target.value || null) as Draft[typeof k])}
        className="h-8"
      />
    </div>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.asset_name.trim() || !draft.fund_id) {
      toast.error("Please fill in the fund and asset name");
      return;
    }
    try {
      if (asset) {
        await update.mutateAsync({ id: asset.id, ...draft });
        toast.success("Asset updated");
      } else {
        await create.mutateAsync(draft);
        toast.success("Asset created");
      }
      onOpenChange(false);
    } catch {
      toast.error("Error saving asset");
    }
  };

  const busy = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
        <DialogHeader>
          <DialogTitle>{asset ? "Edit Asset" : "New Asset"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">

            {/* Fund & Name */}
            <div className="space-y-1">
              <Label className="text-xs">Fund</Label>
              <Select value={draft.fund_id} onValueChange={(v) => set("fund_id", v)}>
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select fund" />
                </SelectTrigger>
                <SelectContent>
                  {funds.map((f) => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Asset Name</Label>
              <Input
                value={draft.asset_name}
                onChange={(e) => set("asset_name", e.target.value)}
                className="h-8"
                required
              />
            </div>

            {/* Thesis */}
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Inv. Thesis</Label>
              <Input
                value={draft.thesis ?? ""}
                onChange={(e) => set("thesis", e.target.value || null)}
                className="h-8"
              />
            </div>

            {/* Financial fields */}
            {dateField("investment_date", "Inv. Date")}
            {numField("acquisition_cost", "Acq. Cost (BRL)")}
            {numField("capital_received", "Cap. Received (BRL)")}
            {numField("future_receivables", "Future Recv. (BRL)")}
            {numField("future_asset_value", "Future Asset Val. (BRL)")}
            {numField("multiple", "CoC")}
            {numField("irr", "IRR (%)")}
            {numField("average_tenor", "Tenor (Y)")}
            {dateField("first_payment_date", "1st Pmt. Date")}
            {numField("first_payment_value", "1st Pmt. Val.")}
            {dateField("last_payment_date", "Last Pmt. Date")}
            {numField("last_payment_value", "Last Pmt. Val.")}

            {/* Structure section */}
            <div className="space-y-1">
              <Label className="text-xs">Structure</Label>
              <Select
                value={draft.structure ?? ""}
                onValueChange={(v) => set("structure", (v || null) as Draft["structure"])}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="True Sale">True Sale</SelectItem>
                  <SelectItem value="Structured">Structured</SelectItem>
                  <SelectItem value="Investment">Investment</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Upfront Amount</Label>
              <Input
                value={draft.upfront_amount ?? ""}
                onChange={(e) => set("upfront_amount", e.target.value || null)}
                className="h-8"
              />
            </div>

            {/* Other Details */}
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Other Details</Label>
              <Textarea
                value={draft.other_details ?? ""}
                onChange={(e) => set("other_details", e.target.value || null)}
                className="min-h-[72px] resize-none text-sm"
              />
            </div>

            {/* Feeder */}
            <div className="col-span-2 space-y-1">
              <Label className="text-xs">Feeder</Label>
              <Input
                value={draft.feeder ?? ""}
                onChange={(e) => set("feeder", e.target.value || null)}
                className="h-8"
              />
            </div>

          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? "Saving..." : asset ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
